import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { getSiteContent } from "../content/siteContent";
import { useRemotePage } from "../remote/RemoteControlContext";

function StepList({ steps }) {
  return (
    <div className="mx-auto mt-8 flex max-w-5xl flex-wrap justify-center gap-3">
      {steps.map((step, index) => (
        <div
          key={step}
          className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-center shadow-lg shadow-slate-950/20 sm:w-44"
        >
          <div className="mx-auto grid h-8 w-8 place-items-center rounded-full bg-sky-300 text-sm font-black text-slate-950">
            {index + 1}
          </div>
          <p className="mt-3 text-xs font-bold uppercase tracking-[0.12em] text-indigo-100/80">{step}</p>
        </div>
      ))}
    </div>
  );
}

function HighlightList({ highlights }) {
  return (
    <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
      {highlights.map((highlight) => (
        <div
          key={highlight}
          className="rounded-2xl border border-pink-300/20 bg-pink-500/10 px-4 py-4 text-center text-sm font-bold uppercase tracking-[0.12em] text-pink-100"
        >
          {highlight}
        </div>
      ))}
    </div>
  );
}

function AboutImage({ src, alt }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-slate-950/55 p-3 shadow-2xl shadow-slate-950/35 ring-1 ring-white/10">
      <img src={src} alt={alt} className="h-auto w-full rounded-2xl object-contain" />
    </div>
  );
}

function WebsiteBackgroundShapes() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute -left-32 top-4 h-96 w-96 rounded-full bg-cyan-400/20 blur-3xl motion-safe:animate-pulse" />
      <div className="absolute -right-16 bottom-0 h-64 w-64 rounded-full bg-fuchsia-500/20 blur-3xl motion-safe:animate-pulse [animation-delay:1.2s]" />
      <div className="absolute left-[45%] top-[6%] h-36 w-36 rounded-full bg-orange-400/10 blur-3xl motion-safe:animate-pulse [animation-delay:700ms]" />
      <div className="absolute left-[6%] top-[14%] select-none text-8xl font-extralight leading-none text-pink-300/25 motion-safe:animate-spin-slow">×</div>
      <div className="absolute bottom-[12%] right-[8%] select-none text-4xl font-extralight leading-none text-amber-300/25 motion-safe:animate-spin-slower">×</div>
      <div className="absolute bottom-[11%] left-[42%] h-8 w-8 rounded-full border-[3px] border-emerald-300/25 motion-safe:animate-bounce" />
      <div className="absolute right-[47%] top-[24%] h-4 w-4 rotate-45 rounded-sm bg-orange-300/30 shadow-[0_0_20px_rgba(253,186,116,0.35)] motion-safe:animate-spin-slow" />
      <div className="absolute bottom-[28%] left-[18%] h-3 w-3 rounded-full bg-yellow-300/40 shadow-[0_0_18px_rgba(253,224,71,0.45)] motion-safe:animate-pulse" />
      <svg
        className="absolute right-[34%] top-[7%] h-28 w-28 text-violet-300/20 motion-safe:animate-spin-slower"
        viewBox="0 0 100 100"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
      >
        <path d="M50 12 90 82H10Z" />
      </svg>
    </div>
  );
}

function Paragraphs({ items }) {
  return items.map((paragraph) => <p key={paragraph}>{paragraph}</p>);
}

export default function AboutPage() {
  const navigate = useNavigate();
  const content = getSiteContent();
  const { about, direction } = content;
  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  useRemotePage({
    id: "about",
    title: about.remote.title,
    subtitle: about.remote.subtitle,
    state: {},
    sections: [{ title: about.remote.sectionTitle, controls: [
      { id: "about.website", type: "button", label: about.remote.website },
      { id: "about.play", type: "button", label: about.remote.play },
      { id: "about.motion", type: "button", label: about.remote.motion },
      { id: "about.tournaments", type: "button", label: about.remote.tournaments },
      { id: "about.games", type: "button", label: about.remote.games },
      { id: "about.plans", type: "button", label: about.remote.plans },
    ] }],
    actions: {
      "about.website": () => jump("about-website"),
      "about.play": () => jump("about-play"),
      "about.motion": () => jump("about-motion"),
      "about.tournaments": () => jump("about-tournaments"),
      "about.games": () => navigate("/"),
      "about.plans": () => navigate("/subscriptions"),
    },
  });

  return (
    <main className="flex-grow" dir={direction}>
      <section className="mx-auto max-w-6xl px-4 pb-10 pt-14 text-center sm:pt-20">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-sky-200/80">{about.hero.eyebrow}</p>
        <h1 className="mt-4 text-4xl font-black tracking-[0.02em] text-white sm:text-5xl">{about.hero.title}</h1>
        <p className="mx-auto mt-5 max-w-3xl text-base leading-8 text-indigo-100/75 sm:text-lg">{about.hero.description}</p>
      </section>

      <section id="about-website" className="relative isolate scroll-mt-24 overflow-hidden border-t border-white/10 bg-indigo-950/50">
        <WebsiteBackgroundShapes />
        <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:py-20">
          <div className="text-start">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-sky-200/75">{about.website.eyebrow}</p>
            <h2 className="mt-4 text-3xl font-black text-white sm:text-4xl">{about.website.title}</h2>
            <div className="mt-6 space-y-4 text-base leading-8 text-indigo-100/78"><Paragraphs items={about.website.paragraphs} /></div>
          </div>
          <div className="space-y-5"><AboutImage src="/about_phone_remote.png" alt={about.website.imageAlt} /></div>
        </div>
      </section>

      <section id="about-play" className="scroll-mt-24 border-t border-white/10">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:py-20">
          <div className="space-y-5"><AboutImage src="/about_website.png" alt={about.play.imageAlt} /></div>
          <div className="text-start">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-sky-200/75">{about.play.eyebrow}</p>
            <h2 className="mt-4 text-3xl font-black text-white sm:text-4xl">{about.play.title}</h2>
            <div className="mt-6 space-y-4 text-base leading-8 text-indigo-100/78"><Paragraphs items={about.play.paragraphs} /></div>
          </div>
          <div className="lg:col-span-2"><StepList steps={about.play.steps} /></div>
        </div>
      </section>

      <section id="about-motion" className="scroll-mt-24 border-t border-white/10 bg-indigo-950/50">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:py-20">
          <div className="text-start lg:order-1">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-pink-200/75">{about.motion.eyebrow}</p>
            <h2 className="mt-4 text-3xl font-black text-white sm:text-4xl">{about.motion.title}</h2>
            <div className="mt-6 space-y-4 text-base leading-8 text-indigo-100/78"><Paragraphs items={about.motion.paragraphs} /></div>
            <HighlightList highlights={about.motion.highlights} />
          </div>
          <div className="lg:order-2"><AboutImage src="/about_kinect.png" alt={about.motion.imageAlt} /></div>
        </div>
      </section>

      <section id="about-tournaments" className="scroll-mt-24 border-y border-white/10">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:py-20">
          <AboutImage src="/about_tournament.png" alt={about.tournaments.imageAlt} />
          <div className="text-start">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-fuchsia-200/75">{about.tournaments.eyebrow}</p>
            <h2 className="mt-4 text-3xl font-black text-white sm:text-4xl">{about.tournaments.title}</h2>
            <p className="mt-6 text-base leading-8 text-indigo-100/78">{about.tournaments.description}</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-20 pt-14">
        <div className="flex flex-col items-center justify-between gap-5 rounded-3xl border border-sky-300/20 bg-sky-300/10 px-6 py-7 text-center shadow-2xl shadow-slate-950/25 sm:flex-row sm:text-start">
          <div>
            <h2 className="text-2xl font-black text-white">{about.callToAction.title}</h2>
            <p className="mt-2 text-indigo-100/70">{about.callToAction.description}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/" className="rounded-full bg-sky-300 px-6 py-3 text-sm font-black uppercase tracking-[0.14em] text-slate-950 transition hover:bg-cyan-200">{about.callToAction.games}</Link>
            <Link to="/subscriptions" className="rounded-full border border-white/15 px-6 py-3 text-sm font-black uppercase tracking-[0.14em] text-indigo-100 transition hover:border-pink-300/60 hover:bg-pink-500/15 hover:text-white">{about.callToAction.plans}</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
